/**
 * Linear, compact-closed, and dagger-compact diagrams: resource
 * ledgers, string diagrams (caps/cups/snake), and dagger involution.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import { STROKE, LABEL, ArrowDefs, ObjectNode, Arrow } from "./primitives.js";

/**
 * LinearResourceLedger — a small table showing three resource tokens and their
 * lifecycle: Available → Consumed → ✗ Cannot reuse.
 */
export function LinearResourceLedger({
  accent = STROKE,
  ariaLabel = "Linear resource ledger showing use-once discipline: each token is consumed exactly once and cannot be reused",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const rows: readonly { name: string; status: "available" | "consumed" | "forbidden" }[] = [
    { name: "Token A", status: "available" },
    { name: "Token B", status: "consumed" },
    { name: "Token C", status: "forbidden" },
  ];
  const rowHeight = 36;
  const headerY = 30;
  const tableTop = headerY + 14;
  const statusLabel = (s: "available" | "consumed" | "forbidden"): string =>
    s === "available" ? "Available" : s === "consumed" ? "Consumed" : "✗ Cannot reuse";
  const statusColor = (s: "available" | "consumed" | "forbidden"): string =>
    s === "available" ? GRAY[900] : s === "consumed" ? GRAY[600] : GRAY[500];
  const indicatorColor = (s: "available" | "consumed" | "forbidden"): string =>
    s === "available" ? accent : s === "consumed" ? GRAY[400] : GRAY[300];
  return (
    <svg
      viewBox="0 0 340 170"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      {/* column headers */}
      <text x={44} y={headerY} fontSize="11" fontWeight={700} fill={GRAY[500]}>
        Resource
      </text>
      <text x={210} y={headerY} fontSize="11" fontWeight={700} fill={GRAY[500]}>
        Status
      </text>
      <line x1={24} y1={tableTop} x2={316} y2={tableTop} stroke={GRAY[300]} strokeWidth={1} />
      {rows.map((row, i) => {
        const y = tableTop + 10 + i * rowHeight;
        const fill = statusColor(row.status);
        return (
          <g key={row.name}>
            <circle cx={32} cy={y + 5} r={5} fill={indicatorColor(row.status)} />
            <text x={48} y={y + 10} fontSize="13" fontWeight={500} fill={LABEL}>
              {row.name}
            </text>
            <text x={210} y={y + 10} fontSize="12" fontWeight={600} fill={fill}>
              {statusLabel(row.status)}
            </text>
            {i < rows.length - 1 && (
              <line
                x1={24}
                y1={y + rowHeight - 6}
                x2={316}
                y2={y + rowHeight - 6}
                stroke={GRAY[300]}
                strokeWidth={0.5}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * StringDiagram — caps, cups, and the snake equation for compact-closed
 * categories.
 *
 * Shows:
 * 1. Wires for objects A and A*
 * 2. A "cup" (∪) for coevaluation η : I → A ⊗ A*
 * 3. A "cap" (∩) for evaluation ε : A* ⊗ A → I
 * 4. The snake equation: a wire with cap + cup simplifies to a straight wire
 */
export function StringDiagram({
  accent = STROKE,
  ariaLabel = "String diagram showing caps, cups, and the snake equation for compact-closed categories",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 480 210"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      {/* ── Left panel: cap and cup ── */}
      <text x={80} y={18} textAnchor="middle" fontSize="11" fontWeight={700} fill={GRAY[500]}>
        cap (ε)
      </text>
      {/* cap: ∩ shape */}
      <path
        d="M 45 55 Q 45 25, 80 25 Q 115 25, 115 55"
        fill="none"
        stroke={accent}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <text x={45} y={72} textAnchor="middle" fontSize="12" fontWeight={600} fill={LABEL}>
        A*
      </text>
      <text x={115} y={72} textAnchor="middle" fontSize="12" fontWeight={600} fill={LABEL}>
        A
      </text>
      <text x={80} y={92} textAnchor="middle" fontSize="11" fill={GRAY[500]}>
        → I
      </text>

      <text x={80} y={118} textAnchor="middle" fontSize="11" fontWeight={700} fill={GRAY[500]}>
        cup (η)
      </text>
      {/* cup: ∪ shape */}
      <path
        d="M 45 135 Q 45 172, 80 172 Q 115 172, 115 135"
        fill="none"
        stroke={accent}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <text x={45} y={130} textAnchor="middle" fontSize="12" fontWeight={600} fill={LABEL}>
        A
      </text>
      <text x={115} y={130} textAnchor="middle" fontSize="12" fontWeight={600} fill={LABEL}>
        A*
      </text>
      <text x={80} y={194} textAnchor="middle" fontSize="11" fill={GRAY[500]}>
        I →
      </text>

      {/* ── Divider ── */}
      <line
        x1={165}
        y1={20}
        x2={165}
        y2={200}
        stroke={GRAY[300]}
        strokeWidth={1}
        strokeDasharray="4 3"
      />

      {/* ── Right panel: snake equation ── */}
      <text x={310} y={18} textAnchor="middle" fontSize="11" fontWeight={700} fill={GRAY[500]}>
        snake equation
      </text>
      {/* Left side: wire with cup then cap (snake identity) */}
      <line x1={230} y1={32} x2={230} y2={52} stroke={accent} strokeWidth={2} />
      {/* cup: ∪ bending right-and-down */}
      <path
        d="M 230 52 Q 230 95, 260 95 Q 290 95, 290 52"
        fill="none"
        stroke={accent}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <line x1={290} y1={52} x2={290} y2={148} stroke={accent} strokeWidth={2} />
      {/* cap: ∩ bending left-and-up */}
      <path
        d="M 290 148 Q 290 105, 260 105 Q 230 105, 230 148"
        fill="none"
        stroke={accent}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <line x1={230} y1={148} x2={230} y2={190} stroke={accent} strokeWidth={2} />

      {/* Equals sign */}
      <text x={345} y={112} textAnchor="middle" fontSize="24" fontWeight={700} fill={GRAY[600]}>
        =
      </text>

      {/* Right side: straight wire */}
      <line x1={400} y1={32} x2={400} y2={190} stroke={accent} strokeWidth={2.5} />
      <text x={414} y={114} fontSize="12" fontWeight={600} fill={LABEL}>
        A
      </text>
    </svg>
  );
}

/**
 * DaggerCompactDiagram — shows a morphism f : A → B alongside its dagger
 * f† : B → A, with a mirror-reflection motif indicating the involution
 * (f†)† = f.
 */
export function DaggerCompactDiagram({
  accent = STROKE,
  ariaLabel = "Dagger compact diagram: morphism f and its dagger adjoint f† with involution (f†)† = f",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 440 170"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />

      {/* ── Left: f : A → B ── */}
      <ObjectNode cx={50} cy={85} label="A" accent={accent} />
      <ObjectNode cx={170} cy={85} label="B" accent={accent} />
      <Arrow x1={64} y1={85} x2={156} y2={85} label="f" markerId={markerId} color={accent} />

      {/* ── Mirror line ── */}
      <line
        x1={220}
        y1={25}
        x2={220}
        y2={145}
        stroke={GRAY[400]}
        strokeWidth={1}
        strokeDasharray="6 3"
      />
      <text x={220} y={18} textAnchor="middle" fontSize="10" fontWeight={600} fill={GRAY[500]}>
        †
      </text>

      {/* ── Right: f† : B → A (mirrored) ── */}
      <ObjectNode cx={270} cy={85} label="B" accent={accent} />
      <ObjectNode cx={390} cy={85} label="A" accent={accent} />
      <Arrow x1={284} y1={85} x2={376} y2={85} label="f†" markerId={markerId} color={accent} />

      {/* ── Involution annotation ── */}
      <text x={220} y={164} textAnchor="middle" fontSize="11" fontWeight={600} fill={GRAY[600]}>
        (f†)† = f
      </text>
    </svg>
  );
}
